"use client";

import { PlayIcon } from "@heroicons/react/24/solid";
import Image, { StaticImageData } from "next/image";
import { useState } from "react";
import YouTube, { YouTubeProps } from "react-youtube";
import { twMerge } from "tailwind-merge";

import { Modal } from "@/components/Modal";
import { Heading4 } from "@/components/text/Heading4";
import { NormalText } from "@/components/text/NormalText";
import { SmallTitle } from "@/components/text/SmallTitle";
import { classNames } from "@/utils/classNames";
import { getSizesString } from "@/utils/getSizesString";

export function InstructionVideo({
  youtubeId,
  title,
  description,
  image,
  titleModal,
  blackMobileText,
  className,
}: {
  youtubeId: string;
  title: string;
  description: string;
  image: StaticImageData;
  titleModal: string;
  blackMobileText: boolean;
  className?: string;
}) {
  const [modalOpen, setModalOpen] = useState(false);

  const opts: YouTubeProps["opts"] = {
    height: "100%",
    width: "100%",
    playerVars: {
      // https://developers.google.com/youtube/player_parameters
      autoplay: 1,
      rel: 0,
    },
  };

  return (
    <>
      <button
        onClick={() => setModalOpen(true)}
        className={twMerge(
          "group flex w-full flex-col items-center gap-8 rounded-3xl bg-primaryLight px-4 py-2 sm:flex-row sm:px-6 sm:py-3",
          className
        )}
      >
        <div className="w-full max-w-[200px] flex-1">
          <div className="aspect-h-9 aspect-w-16 relative">
            <Image
              src={image}
              alt={"Instruction video"}
              className="rounded-3xl object-cover"
              fill={true}
              sizes={getSizesString({
                sm: 100,
                md: 50,
                lg: 33,
                rest: 25,
              })}
            />

            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="rounded-full">
                <PlayIcon className="box-content h-6 w-6 rounded-full bg-gray-900 bg-opacity-80 fill-white p-3 transition-colors group-hover:bg-opacity-50" />
              </span>
              <SmallTitle
                elementType="span"
                className={classNames(
                  "block sm:hidden",
                  blackMobileText ? "text-gray-900" : "text-white"
                )}
              >
                Intro video
              </SmallTitle>
            </div>
          </div>
        </div>

        <div className="hidden flex-1 sm:block">
          <Heading4 elementType="h3" className="mb-2">
            {title}
          </Heading4>
          <NormalText elementType="p" className="mx-auto max-w-xl">
            {description}
          </NormalText>
        </div>
      </button>

      <Modal
        open={modalOpen}
        closeModal={() => setModalOpen(false)}
        widthMode="wide"
        title={titleModal}
      >
        <div className="aspect-h-9 aspect-w-16 mx-auto">
          <YouTube videoId={youtubeId} opts={opts} />
        </div>
      </Modal>
    </>
  );
}
